import { createShapeCaster } from "./casting";
import { Shape, StaticTypeError } from "./core";
import { Binary } from "./encoding";


/**
 * Primitive/Simple shape.
 * 
 * @param type The type that this shape represents - "string", "boolean" or "number"
 */
export const simple = <const T extends Shape.Simple["type"]>(type: T) => ({
  type
}) as const;
/**
 * Literal shape
 * 
 * @param value The specific value this is valid for this shape
 */
export const literal = <const T extends Shape.Type.Simple>(value: T) => ({
  type: 'literal',
  value,
}) as const;
/**
 * Construct a Shape.Compound.Array shape, with the provided
 * argument as the shape of each element in the array.
 * 
 * @param element The shape of all the elements of the array.
 */
export const array = <const T extends Shape>(element: T) => ({
  type: 'array',
  element,
}) as const;
export const tuple = <const T extends Shape[]>(elements: T) => ({
  type: 'tuple',
  elements,
}) as const;

export const unknown = () => ({
  type: 'unknown',
}) as const;
/**
 * Object shape
 * 
 * @param properties - The key/values for each valid property on the object.
 */
export const object = <const T extends { [key: string]: Shape }>(properties: T) => ({
  type: 'object',
  properties,
}) as const
/**
 * @param of The different values that this shape may be.
 */
export const union = <const T extends ReadonlyArray<Shape>>(of: T) => ({
  type: 'union',
  of,
}) as const

/// higher order shapes
/**
 * Create a Discriminated Union shape: A shape that is a union
 * of a set of objects that have a unique value for a field (called the "Discriminator").
 * 
 * 
 * @example
 * ```ts
 * // A discriminated union, where the discriminator is "type";
 * type Animals =
 *  | { type: 'cat', meow: boolean }
 *  | { type: 'dog', woof: boolean };
 * 
 * // Declaring that type as a shape using discriminatedUnion (via s.du)
 * const Animals = s.du('type', {
 *  cat: { meow: s.bool },
 *  dog: { woof: s.bool }
 * })
 * ```
 * @alias `s.du`
 * 
 * @param discriminator C
 * @param map 
 * @returns A regular Union Shape that is a discriminated union based on the values of the provided map.
 */
export const discriminatedUnion = <
  const D extends string,
  const T extends { [key: string]: { [key: string]: Shape } }
>(discriminator: D, map: T): {
  type: 'union',
  of: {
    [Key in keyof T]: {
      type: 'object',
      properties: {
        [d in D]: { type: 'literal', value: Key }
      } & T[Key]
    }
  }[keyof T][],
  cases: {
    [Key in keyof T]: {
      type: 'object',
      properties: {
        [d in D]: { type: 'literal', value: Key }
      } & T[Key]
    }
  },
  map: T,
} => {
  const casesByKey = Object.keys(map).map((key => {
    return [key, s.o({ [discriminator]: s.l(key as any), ...map[key] as any })]
  }))

  return {
    type: 'union',
    map,
    of: casesByKey.map(([,c]) => c as any),
    cases: Object.fromEntries(casesByKey),
  }
}
/**
 * A union where all the cases are a simple type, each of the values of the
 * array argument.
 * @param values An array of values that is each of the literal options for the
 * union
 * @returns A regular Union Shape.
 */
export const literalUnion = <const T extends Shape.Type.Simple[]>(
  values: T
): { type: 'union', of: { [Index in keyof T]: { type: 'literal', value: T[Index] } } } => {
  return {
    type: 'union',
    of: values.map(v => literal(v)) as any,
  }
}

/**
 * Quick names for referencing functions that build shapes
 */
export const s = {
  bool: simple('boolean'),
  num: simple('number'),
  /**
   * The shape of a string (of any length)
   */
  str: simple('string'),

  null: literal(null),

  l: literal,
  a: array,
  t: tuple,
  o: object,
  u: union,


  literal,
  array,
  tuple,
  object,
  union,
  unknown: { type: 'unknown' } as const,

  optional: <T extends Shape.Any>(x: T) => s.u([s.l(null), x] as const),
  du: discriminatedUnion,
  lu: literalUnion,

  Binary: Binary,
  Caster: createShapeCaster,

  meta<const T extends Shape>(inner: T, properties: Record<string, string>) {
    // type test
    ({ type: 'meta', properties, inner }) satisfies Shape.Complex.Meta;

    return { type: 'meta', properties, inner } as const;
  },

  ref: <Type extends Shape.Type.Any, Force = false>() =>
    <TShape extends Shape.Any>(x: TShape):
      Force extends "force" ? Shape.Complex.Ref<Type>
      : Shape.Matches<Type, TShape> extends true
        ? Shape.Complex.Ref<Type>
        : StaticTypeError<"Shape does not meet Type requirements"> =>
      x as any,
}